import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AgentActionKind,
  ConnectorStatus,
  type AgentAction,
  type ExternalSignal,
  type SignalSeverity,
} from '@leanopx/shared';
import { useUiStore, type RailTab } from '@/stores/ui-store';
import { useResizableRail } from '@/hooks/use-resizable-rail';
import { useActiveOrg } from '@/components/providers/auth-provider';
import { useRailContent } from './rail-content';
import { api } from '@/lib/api';
import { warnUnlessAbort } from '@/lib/logger';
import { t, tFormat, type StringKey } from '@/lib/i18n';
import { formatRelativeShort } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

/**
 * Right rail — three tabs (status / threads / watchlist). Per §5.7 of the
 * portal redesign decision log, only `status` is wired in PR1; threads
 * and watchlist render inline placeholders so the tab affordance is
 * visible (and accessible) but doesn't mislead the operator about what's
 * available yet.
 *
 * The rail's leading edge has a 5px drag handle (managed by
 * `useResizableRail`) and a "›" collapse button in the tab strip; the
 * collapsed state shows a vertical "PANEL" reopener (see `<RailReopener>`).
 */

const TABS: ReadonlyArray<{ id: RailTab; label: string }> = [
  { id: 'status', label: 'status' },
  { id: 'threads', label: 'threads' },
  { id: 'watchlist', label: 'watchlist' },
];

export function PortalRail() {
  const railTab = useUiStore((s) => s.railTab);
  const setRailTab = useUiStore((s) => s.setRailTab);
  const setRailOpen = useUiStore((s) => s.setRailOpen);
  const { onMouseDown, isDragging } = useResizableRail();
  // Page-supplied content overrides the default status/threads/watchlist tabs.
  const pageContent = useRailContent();

  if (pageContent) {
    return (
      <aside className="portal-rail" aria-label="Side rail">
        <button
          type="button"
          aria-label="Resize rail"
          title="Drag to resize"
          onMouseDown={onMouseDown}
          className={cn('portal-rail-resize', isDragging && 'is-dragging')}
        />
        <div className="portal-rail-content">
          <div className="mb-3 flex items-center justify-end">
            <button
              type="button"
              onClick={() => setRailOpen(false)}
              aria-label="Collapse rail"
              title="Collapse rail"
              className="rounded-[4px] border border-[var(--color-line-2)] bg-transparent px-1.5 py-px font-mono text-[10px] text-[var(--color-ink-muted)] cursor-pointer hover:text-[var(--color-ink)]"
            >
              ›
            </button>
          </div>
          {pageContent}
        </div>
      </aside>
    );
  }

  return (
    <aside className="portal-rail" aria-label="Side rail">
      <button
        type="button"
        aria-label="Resize rail"
        title="Drag to resize"
        onMouseDown={onMouseDown}
        className={cn('portal-rail-resize', isDragging && 'is-dragging')}
      />
      <div className="portal-rail-content">
        <div
          className="flex items-center gap-4 border-b border-[var(--color-line)] pb-2.5 font-mono text-[11px] uppercase tracking-[0.06em] text-[var(--color-ink-muted)]"
          role="tablist"
          aria-label="Rail tabs"
        >
          {TABS.map((t) => {
            const selected = railTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`portal-rail-tab-${t.id}`}
                aria-controls={`portal-rail-panel-${t.id}`}
                aria-selected={selected}
                tabIndex={selected ? 0 : -1}
                onClick={() => setRailTab(t.id)}
                className={cn(
                  'cursor-pointer pb-0.5 -mb-px border-b border-transparent transition-colors',
                  selected
                    ? 'text-[var(--color-ink)] border-[var(--color-accent)]'
                    : 'hover:text-[var(--color-ink-secondary)]',
                )}
              >
                {t.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setRailOpen(false)}
            aria-label="Collapse rail"
            title="Collapse rail"
            className="ms-auto rounded-[4px] border border-[var(--color-line-2)] bg-transparent px-1.5 py-px font-mono text-[10px] text-[var(--color-ink-muted)] cursor-pointer hover:text-[var(--color-ink)]"
          >
            ›
          </button>
        </div>

        <div
          role="tabpanel"
          id="portal-rail-panel-status"
          aria-labelledby="portal-rail-tab-status"
          hidden={railTab !== 'status'}
          tabIndex={0}
          className="contents"
        >
          {railTab === 'status' && <RailStatusPanel />}
        </div>
        <div
          role="tabpanel"
          id="portal-rail-panel-threads"
          aria-labelledby="portal-rail-tab-threads"
          hidden={railTab !== 'threads'}
          tabIndex={0}
          className="contents"
        >
          {railTab === 'threads' && <RailComingSoon kind="threads" />}
        </div>
        <div
          role="tabpanel"
          id="portal-rail-panel-watchlist"
          aria-labelledby="portal-rail-tab-watchlist"
          hidden={railTab !== 'watchlist'}
          tabIndex={0}
          className="contents"
        >
          {railTab === 'watchlist' && <RailWatchlistPanel />}
        </div>
      </div>
    </aside>
  );
}

interface RailStatus {
  sourcesActive: number;
  sourcesTotal: number;
  signalsActive: number;
  eventsSinceMidnight: number;
  watchlistCrossed: number;
  latestAction: AgentAction | null;
}

/**
 * Right-rail "active surveillance" data. One fan-out fetch on mount per
 * org (connectors + external signals + agent actions today). The
 * legacy `signals.{active,watchlistCrossed}` strap that the old
 * Briefing doc carried is gone post-#166 PR B — the Brief doc is now
 * IMMUTABLE and item-keyed, with no top-level signal counters. We
 * read external signals directly and derive the same two counts
 * client-side (active = total surfaced today; watchlistCrossed =
 * critical-severity rows). Each call fails soft — a single 5xx
 * returns zeros rather than blanking the whole rail.
 */
function useRailStatus(orgId: string): RailStatus | null {
  const [status, setStatus] = useState<RailStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    setStatus(null);
    const today = new Date().toISOString().slice(0, 10);
    const sinceMidnightIso = `${today}T00:00:00.000Z`;
    Promise.all([
      api
        .listConnectors(orgId)
        .then((r) => ({
          active: r.connectors.filter((c) => c.status === ConnectorStatus.enum.active).length,
          total: r.connectors.length,
        }))
        .catch((err) => {
          warnUnlessAbort('rail.connectors load failed', err);
          return { active: 0, total: 0 };
        }),
      api
        .listExternalSignals(orgId, { since: sinceMidnightIso, limit: 50 })
        .then((r) => {
          const sigs: ReadonlyArray<ExternalSignal> = r.signals;
          const critical: SignalSeverity = 'critical';
          return {
            active: sigs.length,
            watchlistCrossed: sigs.filter((s) => s.severity === critical).length,
          };
        })
        .catch((err) => {
          warnUnlessAbort('rail.external-signals load failed', err);
          return { active: 0, watchlistCrossed: 0 };
        }),
      api
        .listAgentActions(orgId, { day: today })
        .then((r) => r.actions)
        .catch((err) => {
          warnUnlessAbort('rail.agent-actions load failed', err);
          return [] as AgentAction[];
        }),
    ]).then(([connectors, signals, actions]) => {
      if (cancelled) return;
      // Actions come back at-DESC, so the first row is the most recent.
      setStatus({
        sourcesActive: connectors.active,
        sourcesTotal: connectors.total,
        signalsActive: signals.active,
        eventsSinceMidnight: actions.length,
        watchlistCrossed: signals.watchlistCrossed,
        latestAction: actions[0] ?? null,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);
  return status;
}

// Verbs for the "agent · acting now" block. AgentActionKind is a closed
// enum, so the mapping is a typed Record — adding a new kind in shared
// surfaces a compile error until a verb lands here.
const AGENT_VERB_KEYS: Record<AgentActionKind, StringKey> = {
  drafted: 'rail.agent.verb.drafted',
  sent: 'rail.agent.verb.sent',
  scheduled: 'rail.agent.verb.scheduled',
  watched: 'rail.agent.verb.watched',
  proposed: 'rail.agent.verb.proposed',
  synthesized: 'rail.agent.verb.synthesized',
  errored: 'rail.agent.verb.errored',
};

function RailStatusPanel() {
  const { id: orgId } = useActiveOrg();
  const status = useRailStatus(orgId);

  // Em-dash placeholder while in flight; once loaded we always render
  // the real number (including 0 — a 0 in this panel is a meaningful
  // "system quiet" signal, unlike the sidebar badge case).
  const fmt = (n: number | undefined): string => (n === undefined ? '—' : String(n));

  const latest = status?.latestAction ?? null;

  return (
    <>
      <div className="rounded-[8px] border border-[var(--color-line)] p-3.5">
        <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--color-ink-muted)]">
          Watching now
        </div>
        <RailStatusGrid
          rows={[
            {
              k: 'sources live',
              v: status ? `${status.sourcesActive} / ${status.sourcesTotal}` : '—',
              tone: 'sage',
            },
            {
              k: 'signals open',
              v: fmt(status?.signalsActive),
              tone: status && status.signalsActive > 0 ? 'amber' : undefined,
            },
            {
              k: 'events 0:00 →',
              v: fmt(status?.eventsSinceMidnight),
            },
            {
              k: 'watchlist crossed',
              v: fmt(status?.watchlistCrossed),
              tone: status && status.watchlistCrossed > 0 ? 'amber' : undefined,
            },
          ]}
        />
      </div>

      <div
        className="rounded-[8px] border p-3.5"
        style={{
          background: 'var(--color-surface)',
          borderColor: 'var(--color-accent-soft)',
        }}
      >
        <div
          className="mb-2 font-mono text-[10px] uppercase tracking-[0.12em]"
          style={{ color: 'var(--color-accent)' }}
        >
          {t('rail.agent.title')}
        </div>
        {status === null && (
          <div className="text-[12.5px] leading-[1.5] text-[var(--color-ink-muted)]">
            {t('common.loading')}
          </div>
        )}
        {status !== null && latest === null && (
          <div className="text-[12.5px] leading-[1.5] text-[var(--color-ink-muted)]">
            {t('rail.agent.empty')}
          </div>
        )}
        {latest !== null && (
          <div className="text-[12.5px] leading-[1.5] text-[var(--color-ink)]">
            {t(AGENT_VERB_KEYS[latest.kind])}{' '}
            <b style={{ color: 'var(--color-accent)' }}>{`"${latest.target}"`}</b>
            {(latest.meta || latest.at) && (
              <>
                <br />
                <span className="font-mono text-[11px] text-[var(--color-ink-muted)]">
                  {latest.meta
                    ? tFormat('rail.agent.meta', {
                        meta: latest.meta,
                        when: formatRelativeShort(latest.at),
                      })
                    : formatRelativeShort(latest.at)}
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function RailStatusGrid({
  rows,
}: {
  rows: Array<{
    k: string;
    v: string;
    tone?: 'amber' | 'sage' | 'rose';
    mono?: boolean;
  }>;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-2 font-mono text-[11px] tracking-[0.04em]">
      {rows.map((row, i) => (
        <span key={`${row.k}-${i}`} className="contents">
          <span className="text-[var(--color-ink-muted)] lowercase">{row.k}</span>
          <span
            className={cn(
              'text-end',
              row.mono && 'text-[10px]',
              row.tone === 'amber' && 'text-[var(--color-accent)]',
              row.tone === 'sage' && 'text-[var(--color-sage)]',
              row.tone === 'rose' && 'text-[var(--color-rose)]',
              !row.tone && 'text-[var(--color-ink)]',
            )}
          >
            {row.v}
          </span>
        </span>
      ))}
    </div>
  );
}

function RailComingSoon({ kind }: { kind: 'threads' | 'watchlist' }) {
  const heading = kind === 'threads' ? 'Active threads' : 'Watchlist';
  return (
    <div className="rounded-[8px] border border-[var(--color-line)] p-3.5">
      <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--color-ink-muted)]">
        {heading}
      </div>
      <p className="text-[12.5px] leading-[1.5] text-[var(--color-ink-secondary)]">
        Coming in a later PR. The {kind} panel will surface live state once the cross-tool graph
        (Phase 3) is wired.
      </p>
    </div>
  );
}

/**
 * Watchlist tab — wires the right-rail to the external-signal feed.
 * Shows the most-recent 6 signals (compact: title + severity dot +
 * source + relative time). Click-through opens the source in a new
 * tab when `sourceUrl` is set; otherwise routes to the
 * `/signals/external` page focused on that signal.
 */
function RailWatchlistPanel() {
  const { id: orgId } = useActiveOrg();
  const [signals, setSignals] = useState<ExternalSignal[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .listExternalSignals(orgId, { limit: 6 })
      .then((r) => {
        if (cancelled) return;
        setSignals(r.signals);
      })
      .catch((err) => {
        if (cancelled) return;
        warnUnlessAbort('rail.watchlist load failed', err);
        setError(t('signals.external.error'));
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return (
    <div className="rounded-[8px] border border-[var(--color-line)] p-3.5">
      <div className="mb-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--color-ink-muted)]">
        <span>{t('signals.external.watchlist.title')}</span>
        <Link
          to="/signals/external"
          className="text-[var(--color-accent)] no-underline hover:underline"
        >
          all →
        </Link>
      </div>
      {error && <p className="text-[12.5px] leading-[1.5] text-[var(--color-rose)]">{error}</p>}
      {!error && signals === null && (
        <p className="text-[12.5px] text-[var(--color-ink-muted)]">{t('common.loading')}</p>
      )}
      {!error && signals !== null && signals.length === 0 && (
        <p className="text-[12.5px] leading-[1.5] text-[var(--color-ink-secondary)]">
          {t('signals.external.watchlist.empty')}
        </p>
      )}
      {!error && signals !== null && signals.length > 0 && (
        <ul className="flex flex-col gap-2">
          {signals.map((s) => {
            // External signals open in a new tab; internal fall-back
            // (no sourceUrl) routes via TanStack Router so we keep
            // SPA navigation instead of a full page reload.
            const rowClass =
              'block rounded-[6px] border border-transparent px-1 py-1 no-underline transition-colors hover:bg-white/[0.03] hover:border-[var(--color-line)]';
            const inner = (
              <div className="flex items-start gap-2">
                <SeverityDot severity={s.severity} />
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-[12.5px] leading-[1.4] text-[var(--color-ink)]">
                    {s.title}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.04em] text-[var(--color-ink-muted)]">
                    <span>{s.source}</span>
                    <span aria-hidden>·</span>
                    <span>{formatRelativeShort(s.publishedAt)}</span>
                  </div>
                </div>
              </div>
            );
            return (
              <li key={s.id}>
                {s.sourceUrl ? (
                  <a
                    href={s.sourceUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={rowClass}
                  >
                    {inner}
                  </a>
                ) : (
                  <Link to="/signals/external" className={rowClass}>
                    {inner}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SeverityDot({ severity }: { severity: SignalSeverity }) {
  const tone =
    severity === 'critical'
      ? 'bg-[var(--color-rose)]'
      : severity === 'watch'
        ? 'bg-[var(--color-accent)]'
        : 'bg-[var(--color-ink-muted)]';
  return (
    <span
      className={cn('mt-1 inline-block size-[6px] shrink-0 rounded-full', tone)}
      aria-label={`severity: ${severity}`}
    />
  );
}

/**
 * The collapsed-rail reopener — a 26px vertical strip with a "‹ PANEL"
 * label that sits in place of the full rail. Mounted by `<AppShell>`
 * when `railOpen === false`.
 */
export function RailReopener() {
  const setRailOpen = useUiStore((s) => s.setRailOpen);
  return (
    <button
      type="button"
      onClick={() => setRailOpen(true)}
      title="Reopen panel"
      aria-label="Reopen panel"
      className="portal-rail-reopen"
    >
      <span className="portal-rail-reopen-l">‹ &nbsp; PANEL</span>
    </button>
  );
}
